import type { MotionPathCmd } from "../schema/timeline";

/**
 * Parse a PowerPoint `p:animMotion@path` string.
 * Tokens: M x y, L x y, C x1 y1 x2 y2 x y, Z, E (end). Lowercase = relative.
 * Coordinates are fractions of slide width/height; `slide` converts them to pt.
 */
export function parseMotionPath(path: string, slide: { w: number; h: number }): MotionPathCmd[] {
  const tokens = path.trim().split(/[\s,]+/).filter(Boolean);
  const out: MotionPathCmd[] = [];
  let cx = 0, cy = 0;
  let i = 0;
  const num = (): number => {
    const t = tokens[i++];
    const v = Number(t);
    if (!Number.isFinite(v)) throw new Error(`motion path: expected number, got ${t}`);
    return v;
  };
  while (i < tokens.length) {
    const cmd = tokens[i++]!;
    const rel = cmd === cmd.toLowerCase() && cmd !== "Z" && cmd !== "E";
    const u = cmd.toUpperCase();
    const ax = (v: number) => (rel ? cx + v : v);
    const ay = (v: number) => (rel ? cy + v : v);
    switch (u) {
      case "M": {
        const x = ax(num()), y = ay(num());
        cx = x; cy = y;
        out.push({ c: "M", x: x * slide.w, y: y * slide.h });
        break;
      }
      case "L": {
        const x = ax(num()), y = ay(num());
        cx = x; cy = y;
        out.push({ c: "L", x: x * slide.w, y: y * slide.h });
        break;
      }
      case "C": {
        const x1 = ax(num()), y1 = ay(num()), x2 = ax(num()), y2 = ay(num()), x = ax(num()), y = ay(num());
        cx = x; cy = y;
        out.push({
          c: "C",
          x1: x1 * slide.w, y1: y1 * slide.h, x2: x2 * slide.w, y2: y2 * slide.h, x: x * slide.w, y: y * slide.h,
        });
        break;
      }
      case "Z":
        out.push({ c: "Z" });
        break;
      case "E":
        i = tokens.length;
        break;
      default:
        throw new Error(`motion path: unknown command ${cmd}`);
    }
  }
  return out;
}

export interface PathSample { x: number; y: number; /** cumulative length fraction 0..1 */ t: number; angle: number }

/** Sample the path by arc length into N points (N clamped to [16, 200]). */
export function sampleMotionPath(path: MotionPathCmd[], n?: number): PathSample[] {
  const pts: { x: number; y: number }[] = [];
  let sx = 0, sy = 0, cx = 0, cy = 0;
  for (const c of path) {
    if (c.c === "M") { sx = cx = c.x; sy = cy = c.y; pts.push({ x: c.x, y: c.y }); }
    else if (c.c === "L") { cx = c.x; cy = c.y; pts.push({ x: c.x, y: c.y }); }
    else if (c.c === "C") {
      const steps = 24;
      for (let k = 1; k <= steps; k++) {
        const t = k / steps, mt = 1 - t;
        const x = mt * mt * mt * cx + 3 * mt * mt * t * c.x1 + 3 * mt * t * t * c.x2 + t * t * t * c.x;
        const y = mt * mt * mt * cy + 3 * mt * mt * t * c.y1 + 3 * mt * t * t * c.y2 + t * t * t * c.y;
        pts.push({ x, y });
      }
      cx = c.x; cy = c.y;
    } else if (c.c === "Z") { cx = sx; cy = sy; pts.push({ x: sx, y: sy }); }
  }
  if (pts.length === 0) return [{ x: 0, y: 0, t: 0, angle: 0 }];
  if (pts.length === 1) return [{ ...pts[0]!, t: 0, angle: 0 }, { ...pts[0]!, t: 1, angle: 0 }];
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!, b = pts[i]!;
    cum.push(cum[i - 1]! + Math.hypot(b.x - a.x, b.y - a.y));
  }
  const total = cum[cum.length - 1]! || 1;
  const count = Math.min(200, Math.max(16, n ?? Math.ceil(total / 4)));
  const out: PathSample[] = [];
  let j = 0;
  for (let k = 0; k < count; k++) {
    const target = (k / (count - 1)) * total;
    while (j < cum.length - 2 && cum[j + 1]! < target) j++;
    const a = pts[j]!, b = pts[j + 1]!;
    const seg = cum[j + 1]! - cum[j]! || 1;
    const f = Math.min(1, Math.max(0, (target - cum[j]!) / seg));
    out.push({
      x: a.x + (b.x - a.x) * f,
      y: a.y + (b.y - a.y) * f,
      t: k / (count - 1),
      angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI,
    });
  }
  return out;
}
